import {
  evaluateDm2016OperationalRelease,
  evaluateDm2016ReleaseGate,
  type Dm2016OperationalReleaseIssue,
  type Dm2016ReleaseArtifacts,
  type Dm2016ReleaseGateIssue,
} from "@rcprofessionale/calc-core";

import z1Json from "../../../docs/calcolatore/trascrizioni/dm2016-z1-v1.draft.json";
import z2Json from "../../../docs/calcolatore/trascrizioni/dm2016-z2-coverage-v2.draft.json";
import z2MatrixJson from "../../../docs/calcolatore/trascrizioni/dm2016-z2-matrix-v1.draft.json";
import z2RulesJson from "../../../docs/calcolatore/trascrizioni/dm2016-z2-selection-rules-v1.draft.json";
import roundingPolicyJson from "../../../docs/calcolatore/rulesets/dm2016-rounding-policy-v1.draft.json";
import legalScopePolicyJson from "../../../docs/calcolatore/rulesets/equo-compenso-legal-scope-v1.draft.json";
import coreRulesetJson from "../../../docs/calcolatore/rulesets/dm2016-annex-i13-core-v1.draft.json";
import paCorpusJson from "../../../docs/calcolatore/casi-doro/professionearchitetto-v0.74-50-scenari.json";
import operationalManifestJson from "../../../docs/calcolatore/releases/equocompenso-dm2016-release-v1.json";
import attestationKeyringJson from "../../../docs/calcolatore/releases/dm2016-attestation-public-keys-v1.json";

export type CorpusGateStatus = "complete" | "partial" | "blocked";

export interface CorpusGate {
  readonly id: string;
  readonly table: "Z-1" | "Z-2";
  readonly label: string;
  readonly confirmed: number;
  readonly total: number;
  readonly remaining: number;
  readonly status: CorpusGateStatus;
  readonly detail: string;
}

export interface Dm2016CorpusStatus {
  readonly sourcePublication: string;
  readonly sourceSha256: string;
  readonly safeForCalculation: boolean;
  readonly status: "operational" | "blocked";
  readonly gates: readonly CorpusGate[];
  readonly completedGates: number;
  readonly totalGates: number;
  readonly releaseGate: {
    readonly ready: boolean;
    readonly issueCount: number;
    readonly blockerGroups: readonly CorpusReleaseBlocker[];
  };
  readonly certificationReview: {
    readonly ready: boolean;
    readonly issueCount: number;
    readonly blockerGroups: readonly CorpusReleaseBlocker[];
  };
}

export interface CorpusReleaseBlocker {
  readonly code: string;
  readonly label: string;
  readonly artifacts: readonly string[];
  readonly occurrences: number;
}

interface Z1Coverage {
  readonly destinations_expected: number;
  readonly g_second_transcription_matches: number;
  readonly descriptions_transcribed: number;
  readonly description_second_transcription_matches: number;
  readonly correspondence_rows_transcribed: number;
  readonly correspondence_second_transcription_matches: number;
  readonly z1_footnotes_transcribed: number;
  readonly z1_footnote_second_transcription_matches: number;
}

interface Z2Coverage {
  readonly normalized_atomic_cells_expected: number;
  readonly second_transcription_matches: number;
  readonly normalized_atomic_cells_independently_confirmed: number;
  readonly row_source_profiles_expected: number;
  readonly row_source_profiles_second_transcription_matches: number;
  readonly row_source_profiles_independently_confirmed: number;
}

interface Z2RulesCoverage {
  readonly source_transcription_second_matches: number;
  readonly source_records_remaining_for_second_transcription: number;
  readonly interpretation_second_matches: number;
  readonly interpretation_records_remaining_for_second_transcription: number;
}

interface CorpusDraft<TCoverage> {
  readonly safe_for_calculation: boolean;
  readonly source: {
    readonly publication: string;
    readonly sha256: string;
  };
  readonly coverage: TCoverage;
}

const releaseIssueLabels: Readonly<Record<string, string>> = {
  STATUS_NOT_APPROVED: "Artefatto normativo non ancora approvato",
  SAFE_FLAG_FALSE: "Abilitazione al calcolo ancora disattivata",
  BLOCKING_ISSUES_OPEN: "Questioni normative bloccanti ancora aperte",
  SECOND_TRANSCRIPTION_MISSING: "Secondo trascrittore indipendente mancante",
  APPROVER_MISSING: "Approvatore normativo mancante",
  APPROVAL_DATE_MISSING: "Data di approvazione mancante",
  APPROVED_FINGERPRINT_MISSING: "Impronta del payload approvato mancante",
  APPROVED_FINGERPRINT_MISMATCH: "Artefatto modificato dopo l’approvazione",
  TRANSCRIBERS_NOT_INDEPENDENT: "Primo e secondo trascrittore non indipendenti",
  NUMERIC_TRANSCRIPTION_INCOMPLETE: "Trascrizione numerica dichiarata incompleta",
  STRUCTURE_INVALID: "Struttura dell’artefatto non valida",
  Z1_SECOND_MATCHES_INCOMPLETE: "Riesame indipendente Z-1 incompleto",
  Z1_CALCULATION_DISABLED: "Calcolo Z-1 mantenuto disabilitato",
  Z2_SOURCE_PROFILE_MATCHES_INCOMPLETE: "Profili sorgente Z-2 ancora incompleti",
  Z2_CALCULATION_DISABLED: "Calcolo Z-2 mantenuto disabilitato",
  Z2_SELECTION_SOURCE_MATCHES_INCOMPLETE: "Fonte di fasce e note ancora da confermare",
  Z2_SELECTION_FIRST_SOURCE_TRANSCRIPTION_INCOMPLETE: "Testo delle 46 condizioni di fascia ancora da trascrivere integralmente",
  Z2_SELECTION_INTERPRETATION_MATCHES_INCOMPLETE: "Interpretazione di fasce e note ancora da approvare",
  Z2_SELECTION_RULES_NOT_APPROVED: "Regole Z-2 non ancora approvate come eseguibili",
  Z2_SELECTION_EXECUTABLE_SEMANTICS_INVALID: "Semantica eseguibile delle regole Z-2 non coerente con l’adjudicazione",
  SOURCE_HASH_MISMATCH: "Impronte della fonte non coincidenti",
  PLACEHOLDER_CONTENT_FORBIDDEN: "Segnaposto sintetico vietato negli artefatti",
  ROUNDING_POLICY_MISSING: "Politica di arrotondamento mancante",
  ROUNDING_POLICY_CALCULATION_DISABLED: "Politica di arrotondamento non abilitata",
  ROUNDING_POLICY_ISSUE_OPEN: "Approvazione degli arrotondamenti ancora aperta",
  ROUNDING_POLICY_SEMANTICS_INVALID: "Politica di arrotondamento non coerente con il motore",
  TECHNICAL_APPROVER_MISSING: "Approvatore tecnico degli arrotondamenti mancante",
  LEGAL_SCOPE_POLICY_MISSING: "Matrice del perimetro legale mancante",
  LEGAL_APPROVER_MISSING: "Approvatore legale nominativo mancante",
  LEGAL_SCOPE_VERSION_MISMATCH: "Versione della matrice legale non coerente con il motore",
  LEGAL_SCOPE_EXECUTABLE_CASE_MISMATCH: "Casi legali non coerenti con il classificatore",
  LEGAL_SCOPE_CASE_INVENTORY_INVALID: "Inventario dei casi legali incompleto",
};

const artifactLabels: Readonly<
  Record<Dm2016ReleaseGateIssue["artifact"], string>
> = {
  bundle: "Bundle",
  z1: "Z-1",
  z2Coverage: "Copertura Z-2",
  z2Matrix: "Matrice Z-2",
  z2SelectionRules: "Regole Z-2",
  roundingPolicy: "Arrotondamenti",
  legalScopePolicy: "Perimetro legale",
};

function aggregateReleaseIssues(
  issues: readonly (Dm2016ReleaseGateIssue | Dm2016OperationalReleaseIssue)[],
): CorpusReleaseBlocker[] {
  const groups = new Map<
    string,
    { label: string; artifacts: Set<string>; occurrences: number }
  >();
  for (const issue of issues) {
    const current = groups.get(issue.code) ?? {
      label: releaseIssueLabels[issue.code] ?? issue.code,
      artifacts: new Set<string>(),
      occurrences: 0,
    };
    current.artifacts.add(artifactLabels[issue.artifact as keyof typeof artifactLabels] ?? issue.artifact);
    current.occurrences += 1;
    groups.set(issue.code, current);
  }
  return [...groups.entries()].map(([code, group]) => ({
    code,
    label: group.label,
    artifacts: [...group.artifacts],
    occurrences: group.occurrences,
  }));
}

function makeGate(
  id: string,
  table: CorpusGate["table"],
  label: string,
  confirmed: number,
  total: number,
  detail: string,
): CorpusGate {
  if (!Number.isInteger(confirmed) || !Number.isInteger(total)) {
    throw new Error(`Conteggio non intero per il controllo ${id}`);
  }
  if (total <= 0 || confirmed < 0 || confirmed > total) {
    throw new Error(`Conteggio incoerente per il controllo ${id}`);
  }

  const remaining = total - confirmed;
  return {
    id,
    table,
    label,
    confirmed,
    total,
    remaining,
    status: remaining === 0 ? "complete" : confirmed === 0 ? "blocked" : "partial",
    detail,
  };
}

export async function buildDm2016CorpusStatus(): Promise<Dm2016CorpusStatus> {
  const z1 = z1Json as CorpusDraft<Z1Coverage>;
  const z2 = z2Json as CorpusDraft<Z2Coverage>;
  const rules = z2RulesJson as CorpusDraft<Z2RulesCoverage>;
  const releaseResult = await evaluateDm2016ReleaseGate({
    z1: z1Json,
    z2Coverage: z2Json,
    z2Matrix: z2MatrixJson,
    z2SelectionRules: z2RulesJson,
    roundingPolicy: roundingPolicyJson,
    legalScopePolicy: legalScopePolicyJson,
  } satisfies Dm2016ReleaseArtifacts);
  const operationalResult = await evaluateDm2016OperationalRelease({
    manifest: operationalManifestJson,
    expectedRulesetVersion: coreRulesetJson.version,
    artifacts: {
      z1_corpus: z1Json,
      z2_coverage: z2Json,
      z2_corpus: z2MatrixJson,
      selection_rules: z2RulesJson,
      rounding_policy: roundingPolicyJson,
      professionearchitetto_50_case_corpus: paCorpusJson,
      core_ruleset: coreRulesetJson,
      attestation_public_keyring: attestationKeyringJson,
    },
  });

  if (
    z1.source.sha256 !== z2.source.sha256
    || z1.source.sha256 !== rules.source.sha256
    || z1.source.publication !== z2.source.publication
    || z1.source.publication !== rules.source.publication
  ) {
    throw new Error("Le trascrizioni D.M. 2016 non puntano alla stessa fonte ufficiale");
  }

  if (z1.safe_for_calculation || z2.safe_for_calculation || rules.safe_for_calculation) {
    throw new Error("Un artefatto D.M. 2016 risulta prematuramente abilitato al calcolo");
  }

  const gates: CorpusGate[] = [
    makeGate(
      "z1-g-values",
      "Z-1",
      "Coefficienti G",
      z1.coverage.g_second_transcription_matches,
      z1.coverage.destinations_expected,
      "Confronto numerico indipendente completo sulle 61 destinazioni funzionali.",
    ),
    makeGate(
      "z1-descriptions",
      "Z-1",
      "Descrizioni",
      z1.coverage.description_second_transcription_matches,
      z1.coverage.descriptions_transcribed,
      "Le divergenze testuali non cambiano automaticamente il calcolo, ma devono essere chiuse prima della pubblicazione.",
    ),
    makeGate(
      "z1-correspondences",
      "Z-1",
      "Corrispondenze storiche",
      z1.coverage.correspondence_second_transcription_matches,
      z1.coverage.correspondence_rows_transcribed,
      "Serve un secondo riscontro cieco per ogni corrispondenza riportata dalla Tavola Z-1.",
    ),
    makeGate(
      "z1-footnote",
      "Z-1",
      "Nota di tavola",
      z1.coverage.z1_footnote_second_transcription_matches,
      z1.coverage.z1_footnotes_transcribed,
      "La nota 1 è isolata e resta non approvata finché non coincide con una seconda trascrizione.",
    ),
    makeGate(
      "z2-q-cells",
      "Z-2",
      "Celle dei coefficienti Q",
      z2.coverage.normalized_atomic_cells_independently_confirmed,
      z2.coverage.normalized_atomic_cells_expected,
      z2.coverage.normalized_atomic_cells_independently_confirmed
        === z2.coverage.normalized_atomic_cells_expected
        ? "Tutte le 1.290 coordinate sono confermate dalla seconda trascrizione o da adjudicazione cieca documentata."
        : "Le coordinate residue richiedono un riesame cieco prima di qualunque uso professionale.",
    ),
    makeGate(
      "z2-source-profiles",
      "Z-2",
      "Profili di riga e colonna",
      z2.coverage.row_source_profiles_independently_confirmed,
      z2.coverage.row_source_profiles_expected,
      "Il controllo preserva geometria, colonne applicabili e celle non applicabili; le divergenze sono risolte soltanto con adjudicazione documentata sulla fonte.",
    ),
    makeGate(
      "z2-rule-sources",
      "Z-2",
      "Fasce e note: fonte",
      rules.coverage.source_transcription_second_matches,
      rules.coverage.source_transcription_second_matches
        + rules.coverage.source_records_remaining_for_second_transcription,
      "Prima si conferma ciò che la fonte stampa: soglie, ordine, note e condizioni.",
    ),
    makeGate(
      "z2-rule-interpretations",
      "Z-2",
      "Fasce e note: interpretazione",
      rules.coverage.interpretation_second_matches,
      rules.coverage.interpretation_second_matches
        + rules.coverage.interpretation_records_remaining_for_second_transcription,
      "L’algoritmo eseguibile è un controllo distinto e non può approvarsi insieme alla trascrizione.",
    ),
  ];

  return {
    sourcePublication: z1.source.publication,
    sourceSha256: z1.source.sha256,
    safeForCalculation: operationalResult.calculationEnabled,
    status: operationalResult.ready ? "operational" : "blocked",
    gates,
    completedGates: gates.filter((gate) => gate.status === "complete").length,
    totalGates: gates.length,
    releaseGate: {
      ready: operationalResult.ready,
      issueCount: operationalResult.issues.length,
      blockerGroups: aggregateReleaseIssues(operationalResult.issues),
    },
    certificationReview: {
      ready: releaseResult.ready,
      issueCount: releaseResult.issues.length,
      blockerGroups: aggregateReleaseIssues(releaseResult.issues),
    },
  };
}
